import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { searchLocations, type LocationMatch } from "@/lib/location";
import type { CountyIndexEntry } from "@/lib/geo/county-shapes";

/**
 * One search box for the whole map: ZIP, city, county, or state. It shares
 * src/lib/location.ts with the homepage checker rather than reimplementing
 * resolution, and it only searches counties once the caller has actually loaded the
 * county index — typing "tex" on a cold page must not pull 78KB.
 */
export function MapSearch({
  onPick,
  onNeedCounties,
  counties,
  placeholder = "ZIP, city, county, or state",
}: {
  onPick: (match: LocationMatch) => void;
  /** Called on first meaningful keystroke so the parent can lazily fetch the index. */
  onNeedCounties: () => void;
  counties: CountyIndexEntry[] | null;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const listId = useId();
  const inputId = useId();
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (query.trim().length >= 2) onNeedCounties();
  }, [query, onNeedCounties]);

  const results = useMemo(
    () => searchLocations(query, { counties: counties ?? undefined, limit: 7 }),
    [query, counties],
  );

  useEffect(() => setHighlight(0), [query]);

  // Close the suggestions when focus or a click leaves the combobox.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const choose = (match: LocationMatch) => {
    onPick(match);
    setQuery(match.label);
    setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative">
      <label htmlFor={inputId} className="sr-only">
        Search the map by ZIP code, city, county, or state
      </label>
      <Search
        className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        aria-hidden
      />
      <input
        id={inputId}
        type="search"
        role="combobox"
        aria-expanded={open && results.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && results.length > 0 ? `${listId}-${highlight}` : undefined}
        autoComplete="off"
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!results.length) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setHighlight((h) => (h + 1) % results.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => (h - 1 + results.length) % results.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            choose(results[highlight] ?? results[0]);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
        className="h-12 w-full rounded-sm border-[1.5px] border-foreground/70 bg-card pl-10 pr-10 text-base text-foreground outline-none placeholder:text-muted-foreground/80 focus:border-primary focus-visible:ring-2 focus-visible:ring-primary/25 sm:text-sm"
      />
      {query && (
        <button
          type="button"
          onClick={() => {
            setQuery("");
            setOpen(false);
          }}
          aria-label="Clear the search"
          className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-md text-muted-foreground hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      )}

      {open && results.length > 0 && (
        <ul
          id={listId}
          role="listbox"
          aria-label="Search results"
          className="absolute z-20 mt-1 w-full overflow-hidden rounded-sm border border-border bg-popover shadow-lift"
        >
          {results.map((r, i) => (
            // The option IS the row — a listbox option may not contain a button, and
            // the input keeps focus throughout via aria-activedescendant.
            <li
              key={`${r.method}-${r.county?.fips ?? r.stateAbbr}`}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === highlight}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in the input
                choose(r);
              }}
              className={`flex cursor-pointer items-center justify-between gap-3 px-3 py-2.5 text-sm ${
                i === highlight ? "bg-secondary text-foreground" : "text-foreground"
              }`}
            >
              <span className="truncate">{r.label}</span>
              <span className="label-caps shrink-0 text-muted-foreground">
                {r.method === "county" ? "County" : r.method === "state" ? "State" : "Location"}
              </span>
            </li>
          ))}
        </ul>
      )}
      {query.trim().length > 1 && results.length === 0 && (
        <p className="mt-2 text-xs text-muted-foreground">
          No match for &ldquo;{query.trim()}&rdquo;. Try a 5-digit ZIP, a county name, or a state.
        </p>
      )}
    </div>
  );
}
